import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import Map from "ol/Map.js";
import View from "ol/View.js";
import TileLayer from "ol/layer/Tile.js";
import OSM from "ol/source/OSM";
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Draw, Select } from "ol/interaction";
import { WKT } from "ol/format";
import GeoJSON from 'ol/format/GeoJSON';
import { defaults as defaultControls } from 'ol/control';
import { Style, Fill, Stroke, Circle, Text } from 'ol/style';
import { fromLonLat, transform, useGeographic } from 'ol/proj';
import "ol/ol.css";

import { useGeo } from '../context/GeoContext';


const MapView = React.forwardRef(({ onClose, partido, onFeatureDataChange, initialFeatures = [] }, ref) => {
    // Habilita el uso de coordenadas geográficas (lat/lon) directamente sin transformaciones explícitas si tus datos están en 4326
    useGeographic();
    const { drawnFeatures, handleFeaturesUpdate, loadFeaturesForObra, clearFeatures, selectedObraId, deleteFeature } = useGeo(); // Consume el contexto

    const mapRef = useRef(null);
    const [map, setMap] = useState(null);
    const drawInteractionRef = useRef(null);
    const selectInteractionRef = useRef(null);
    // Usar drawnFeatures del contexto en lugar de mantener estado local
    const featureData = drawnFeatures;
    
    // Efecto para notificar cambios en featureData
    useEffect(() => {
        if (onFeatureDataChange) {
            onFeatureDataChange(drawnFeatures);
        }
    }, [drawnFeatures, onFeatureDataChange]);

    // Efecto para limpiar el componente
    useEffect(() => {
        return () => {
            if (vectorSource.current) {
                vectorSource.current.clear();
            }
            if (selectInteractionRef.current) {
                map?.removeInteraction(selectInteractionRef.current);
            }
            if (drawInteractionRef.current) {
                map?.removeInteraction(drawInteractionRef.current);
            }
        };
    }, []);
    const vectorSource = useRef(new VectorSource()); // Mantener como ref para persistencia
    
    // Exponer funciones a través de ref
    useImperativeHandle(ref, () => ({
        clearAllFeatures: () => {
            if (vectorSource.current) {
                vectorSource.current.clear();
                console.log('Todas las features han sido eliminadas del mapa');
            }
        }
    }));

    // Estilo base para las features
    const defaultFeatureStyle = new Style({ 
        fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)',
        }),
        stroke: new Stroke({
            color: '#ffcc33',
            width: 2,
        }),
        image: new Circle({
            radius: 7,
            fill: new Fill({
                color: '#ffcc33',
            }),
        }),
    });

    // Capa vectorial principal para las features dibujadas, con estilo que incluye texto
    const vectorLayerRef = useRef(
        new VectorLayer({
            source: vectorSource.current,
            // La función de estilo se ejecutará para cada feature
            style: (feature) => {
                const labelText = feature.get('display_label'); // Obtener la etiqueta personalizada
                return new Style({
                    fill: new Fill({
                        color: 'rgba(255, 255, 255, 0.2)',
                    }),
                    stroke: new Stroke({
                        color: '#ffcc33',
                        width: 2,
                    }),
                    image: new Circle({
                        radius: 7,
                        fill: new Fill({
                            color: '#ffcc33',
                        }),
                    }),
                    text: new Text({
                        text: labelText ? String(labelText) : '', // Asegurarse de que el texto sea una cadena
                        fill: new Fill({
                            color: 'red', // CAMBIO CLAVE: Color de texto rojo para asegurar visibilidad
                        }),
                        stroke: new Stroke({
                            color: '#fff', // Trazo blanco para mejor legibilidad
                            width: 3,
                        }),
                        font: '14px sans-serif', // Aumentar ligeramente el tamaño de la fuente
                        offsetY: -20, // Ajustar la posición vertical de la etiqueta
                        textAlign: 'center', // Alineación central
                        textBaseline: 'bottom', // Línea base en la parte inferior
                        placement: 'point' // Asegurar que el texto se posicione como un punto
                    }),
                });
            }
        })
    );   
    
    // Efecto para depurar featureData ---> SE VA
    /*
    useEffect(() => {
        console.log('featureData actualizado:', featureData);
        console.log('Número de features:', featureData.length);
        if (featureData.length > 0) {
            console.log('Primera feature:', {
                id: featureData[0].id,
                featureId: featureData[0].feature.getId(), // Usar getId()
                hasWKT: !!featureData[0].wkt
            });
        }
    }, [featureData]);
    */
    
    const [selectedDrawType, setSelectedDrawType] = useState('none');
    const [selectedWKTIndex, setSelectedWKTIndex] = useState(null);
    const [highlightSource, setHighlightSource] = useState(new VectorSource());

    const [geoJsonLayer, setGeoJsonLayer] = useState(null);//para partido_geom


    useEffect(() => {
        // Estilo para las features resaltadas
        const highlightLayer = new VectorLayer({
            source: highlightSource,
            style: new Style({ 
                stroke: new Stroke({
                    color: '#007bff',
                    width: 3,
                }),
                fill: new Fill({
                    color: 'rgba(0, 123, 255, 0.5)',
                }),
                image: new Circle({
                    radius: 10,
                    fill: new Fill({
                        color: '#007bff',
                    }),
                }),
            }),
        });

        // Capa para resaltado temporal (hover)
        const hoverVectorSource = new VectorSource();
        const hoverVectorLayer = new VectorLayer({
            source: hoverVectorSource,
            style: new Style({
                stroke: new Stroke({
                    color: '#00ff00', // Color verde para el resaltado
                    width: 3,
                }),
                fill: new Fill({
                    color: 'rgba(0, 255, 0, 0.3)',
                }),
                image: new Circle({
                    radius: 10,
                    fill: new Fill({
                        color: '#00ff00',
                    }),
                }),
            }),
        });

        // Añadir la capa de hover al mapa
        //initialMap.addLayer(hoverVectorLayer);


        const initialMap = new Map({
            target: mapRef.current,
            layers: [
                new TileLayer({
                    source: new OSM(),
                }),
                vectorLayerRef.current, // Tu capa vectorial principal para features dibujadas
                highlightLayer, // Capa para resaltar features seleccionadas
                //hoverVectorLayer // Capa para resaltar features al pasar el mouse
            ],
            view: new View({
                center: [-60.50, -36.67], // Coordenadas en EPSG:4326 gracias a useGeographic()
                zoom: 5,
            }),
            controls: defaultControls({
                attribution: false,
                zoom: true,
            }),
        });

        setMap(initialMap);

        // Interacción para hover
        /*
        let lastHoveredFeature = null;
        initialMap.on('pointermove', (e) => {
            const features = initialMap.getFeaturesAtPixel(e.pixel, {
                layerFilter: (layer) => layer === vectorLayerRef.current,
                hitTolerance: 5
            });

            if (features.length > 0) {
                const feature = features[0];

                // Resaltar solo si es una feature diferente
                if (feature !== lastHoveredFeature) {
                    hoverVectorSource.clear();
                    hoverVectorSource.addFeature(feature);
                    lastHoveredFeature = feature;

                    // Cambiar el cursor a pointer
                    initialMap.getTargetElement().style.cursor = 'pointer';
                }
            } else {
                // Limpiar resaltado y restaurar cursor
                if (lastHoveredFeature) {
                    hoverVectorSource.clear();
                    lastHoveredFeature = null;
                    initialMap.getTargetElement().style.cursor = '';
                }
            }
        });*/


        const selectInteraction = new Select({
            layers: [vectorLayerRef.current], // Seleccionar de tu capa vectorial principal
            style: null // Usar estilo de selección por defecto
        });

        // Configurar el manejador de selección
        const handleSelect = (event) => {
            console.log('Evento de selección:', event);
            handleFeatureSelectOnMap(event.selected);
        };

        // Agregar el manejador de eventos
        selectInteraction.on('select', handleSelect);
        
        // Guardar referencia para poder limpiar después
        selectInteractionRef.current = selectInteraction; // Almacenar la interacción directamente
        
        // Agregar la interacción al mapa
        initialMap.addInteraction(selectInteraction);


        return () => {
            // Limpiar interacciones y establecer el objetivo a indefinido
            initialMap.setTarget(undefined);
            if (selectInteractionRef.current) {
                initialMap.removeInteraction(selectInteractionRef.current);
                selectInteractionRef.current.un('select', handleSelect); // Desuscribirse
            }
            if (drawInteractionRef.current) {
                initialMap.removeInteraction(drawInteractionRef.current);
            }
        };
    }, []);

    // Efecto para cargar features del contexto al montar el componente - 15 JUL 2025
    useEffect(() => {
        if (!map) return;
        
        if (drawnFeatures.length > 0) {
            console.log('Cargando features del contexto:', drawnFeatures.length);
            const features = [];
            const wktFormat = new WKT();
            
            drawnFeatures.forEach(item => {
                try {
                    if (item.wkt) {
                        const feature = wktFormat.readFeature(item.wkt, {
                            dataProjection: 'EPSG:4326',
                            featureProjection: 'EPSG:4326'
                        });
                        feature.setId(item.id);
                        feature.set('display_label', item.feature?.get('display_label') || '');
                        features.push(feature);
                    }
                } catch (error) {
                    console.error('Error al cargar feature:', error);
                }
            });
            
            if (features.length > 0) {
                vectorSource.current.clear();
                vectorSource.current.addFeatures(features);
            }
        }
    }, [map]); // Solo dependemos de map

    // Efecto para cargar geometría del partido
    useEffect(() => {
        if (!map || !partido) return;

        const fetchPartidoGeom = async () => {
            try {
                const response = await fetch(`/api/partido_geom/${partido}`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const geojsonData = await response.json();

                if (geojsonData && geojsonData.type === 'FeatureCollection' && geojsonData.features.length > 0) {
                    const vectorSourcePartido = new VectorSource({ // Usar una nueva fuente para la geometría del partido
                        features: new GeoJSON().readFeatures(geojsonData, {
                            featureProjection: 'EPSG:4326', // Leer en la proyección del mapa (4326)
                            dataProjection: 'EPSG:4326'    // Los datos están en 4326
                        }),
                    });

                    // Estilo para la geometría del partido
                    const partidoStyle = new Style({
                        fill: new Fill({
                            color: 'rgba(255, 0, 0, 0.5)', // Relleno rojo semi-transparente
                        }),
                        stroke: new Stroke({
                            color: 'red',
                            width: 2,
                        }),
                        image: new Circle({
                            radius: 5,
                            fill: new Fill({ color: 'red' }),
                            stroke: new Stroke({ color: 'white', width: 1 }),
                        }),
                    });

                    // Si ya existe una capa GeoJSON, la removemos antes de añadir la nueva
                    if (geoJsonLayer) {
                        map.removeLayer(geoJsonLayer);
                    }

                    const newGeoJsonLayer = new VectorLayer({
                        source: vectorSourcePartido, // Usar la nueva fuente
                        style: partidoStyle,
                    });

                    map.addLayer(newGeoJsonLayer);
                    setGeoJsonLayer(newGeoJsonLayer);

                    // Hacer zoom a la extensión de la geometría
                    const extent = vectorSourcePartido.getExtent();
                    map.getView().fit(extent, {
                        padding: [50, 50, 50, 50], // Añadir un poco de padding
                        duration: 1000, // Duración de la animación en ms
                    });
                } else {
                    console.warn('No se encontraron geometrías para el partido:', partido);
                }
            } catch (error) {
                console.error('Error fetching partido geometry:', error);
            }
        };

        fetchPartidoGeom();

        // Cleanup: remover la capa cuando el componente se desmonte o el partido cambie
        return () => {
            if (map && geoJsonLayer) {
                map.removeLayer(geoJsonLayer);
                setGeoJsonLayer(null); // Limpiar el estado también
            }
        };
    }, [map, partido ]); // //geoJsonLayer //Dependencias: el mapa, el número de partido, y geoJsonLayer para limpieza


    const addDrawInteraction = (type) => {
        setSelectedDrawType(type);
        if (!map) return;

        // Desactivar la interacción de selección si existe
        if (selectInteractionRef.current) {
            selectInteractionRef.current.setActive(false);
        }

        // Eliminar la interacción de dibujo anterior si existe
        if (drawInteractionRef.current) {
            map.removeInteraction(drawInteractionRef.current);
            drawInteractionRef.current = null;
        }

        // Si el tipo no es 'none', agregar nueva interacción de dibujo
        if (type !== "none") {
            const drawInteraction = new Draw({
                source: vectorSource.current, // Usar directamente tu ref de vectorSource
                type: type,
            });

            drawInteraction.on("drawend", (evt) => {
                const feature = evt.feature;
                const wktFormat = new WKT();
                const uuid = crypto.randomUUID(); // Generar UUID
                
                // Establecer el ID para la feature
                feature.setId(uuid); 
                // Almacenar el ol_uid interno como una propiedad personalizada para mostrarlo como etiqueta
                feature.set('display_label', feature.ol_uid);
                feature.changed(); // Notificar a OpenLayers que la feature ha cambiado
                
                // *** CAMBIO CLAVE: Forzar la actualización de la fuente para que las etiquetas se muestren ***
                vectorSource.current.changed();

                // La geometría de la feature ya está en EPSG:4326 debido a useGeographic()
                // No se necesita transformación explícita para WKT si se quiere en 4326
                const featureWKT = wktFormat.writeFeature(feature);

                // Crear el nuevo ítem de feature
                const newFeatureDataItem = {
                    id: uuid, // Usar el UUID generado como ID principal
                    wkt: featureWKT,
                    feature: feature, // Almacenar la feature original (en la proyección del mapa)
                    ol_uid: feature.ol_uid // Mantener ol_uid aquí si el usuario lo desea en la estructura de datos
                };
                
                console.log('Nueva feature agregada:', {
                    id: uuid,
                    featureId: feature.getId(),
                    displayLabel: feature.get('display_label'),
                    feature: feature
                });

                // Añadir la feature a la fuente vectorial si no está ya allí
                // Este paso es crucial para que la feature se renderice en el mapa.
                if (!vectorSource.current.getFeatureById(uuid)) {
                    vectorSource.current.addFeature(feature);
                    console.log('Feature añadida a vectorSource.current:', feature.getId());
                } else {
                    console.log('La feature ya existe en vectorSource.current:', feature.getId());
                }

                handleFeaturesUpdate(newFeatureDataItem);
                
                // Actualizar el estado interno // YA NO SE USA ESTADO INTERNO
                /*setFeatureData(oldFeatureData => {
                    const newData = [...oldFeatureData, newFeatureDataItem];
                    console.log('featureData actualizado. Nuevo tamaño:', newData.length);
                    
                    
                    //handleFeaturesUpdate(newData); // Pasa el array completo actualizado
                    return newData;
                });*/               
                
                setSelectedWKTIndex(null);
                highlightSource.clear();
                
                // Reactivar la interacción de selección
                if (selectInteractionRef.current) {
                    selectInteractionRef.current.setActive(true);
                }
            });

            // Añadir la interacción de dibujo al mapa
            map.addInteraction(drawInteraction);
            drawInteractionRef.current = drawInteraction;
        } else {
            // Si el tipo es 'none', reactivar la interacción de selección
            if (selectInteractionRef.current) {
                selectInteractionRef.current.setActive(true);
            }
        }
    }
    const renderWKTList = () => {
        /*
        //DEJAR PENDIENTE
        // Crear una copia invertida del array featureData
        const reversedFeatures = [...featureData].reverse();
        */

        return drawnFeatures.map((featureItem, index) => (
            <li
                key={featureItem.id} // Usar featureItem.id como clave
                data-feature-id={featureItem.id} // Atributo personalizado para facilitar la selección
                className={`my-2 p-3 rounded-md border-2 transition-colors duration-200 list-none cursor-pointer 
                    ${selectedWKTIndex === index 
                        ? 'bg-indigo-100 border-indigo-500 text-indigo-800 shadow-md' 
                        : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'}`}
                onClick={() => handleWKTListItemClick(index)}
            >
                <span className="font-mono text-sm">ID: {featureItem.ol_uid} - {featureItem.wkt.substring(0, 50)}...</span>
                <button
                    onClick={(e) => {
                        e.stopPropagation(); // Evitar que onClick del <li> se ejecute
                        handleDeleteItemFromList(featureItem.id);
                    }}
                    className="ml-2 p-1 bg-red-500 text-white rounded-md text-xs hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                >
                    Eliminar
                </button>
            </li>
        ));
    };

    const handleDeleteItemFromList = (featureIdToDelete) => {
        console.log("featureIdToDelete", featureIdToDelete);
        
        // Obtener la feature a eliminar de la fuente vectorial
        //const featureToDelete = featureData[indexToDelete]?.feature;
        // 1. Eliminar el elemento del estado featureData
        //const updatedFeatureData = featureData.filter((_, idx) => idx !== indexToDelete);
        //setFeatureData(updatedFeatureData);
        //deleteFeature(updatedFeatureData[indexToDelete].id);

        // 1. Obtener la feature de OpenLayers para eliminarla del source
        //const featureToDeleteFromMap = vectorSource.current.getFeatureById(featureIdToDelete);
        // Get the feature object from the vector source
        const featureToDelete = vectorSource.current.getFeatureById(featureIdToDelete);

        // 2. Eliminar la feature del Contexto (GeoContext)
        deleteFeature(featureIdToDelete); // Llama a la función del contexto
        
    
        //2. Eliminar la feature de la fuente vectorial de OpenLayers
        /*if (featureIdToDelete && vectorSource.current.getFeatureById(featureIdToDelete)) {
            vectorSource.current.removeFeature(featureIdToDelete);
            console.log('Feature eliminada de vectorSource:', featureIdToDelete);
        }*/       
        
        if (featureToDelete) {
            vectorSource.current.removeFeature(featureToDelete);
            console.log('Feature eliminada de vectorSource:', featureIdToDelete);
        }
    
        // 3. Limpiar el resaltado y deseleccionar
        highlightSource.clear();
        setSelectedWKTIndex(null);
        if (selectInteractionRef.current) {
            selectInteractionRef.current.getFeatures().clear();
        }
    };


    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                // Leer features asumiendo que están en EPSG:4326 desde GeoJSON y las proyecta a la vista del mapa (4326)
                const geojsonFeatures = new GeoJSON().readFeatures(e.target.result, {
                    featureProjection: 'EPSG:4326', // Proyección del mapa
                    dataProjection: 'EPSG:4326'    // Proyección de los datos GeoJSON
                });

                const newFeatureDataFromGeoJSON = geojsonFeatures.map(feature => {
                    const uuid = crypto.randomUUID();
                    feature.setId(uuid); // Establecer ID para la feature
                    // Para features cargadas desde GeoJSON, no hay un 'ol_uid' interno significativo.
                    // Si deseas mostrar etiquetas para estas features, deberías establecer una propiedad 'display_label'
                    // basada en alguna propiedad existente en el GeoJSON o generar una nueva.
                    // Por ejemplo: feature.set('display_label', feature.get('name') || uuid.substring(0, 8));
                    
                    // El WKT se genera directamente de la feature que ya está en 4326
                    const wktFormat = new WKT();
                    const featureWKT4326 = wktFormat.writeFeature(feature);
                    
                    return {
                        id: uuid,
                        wkt: featureWKT4326,
                        feature: feature // Almacenar feature en la proyección del mapa
                    };
                });

                //setFeatureData(newFeatureDataFromGeoJSON);
                vectorSource.current.clear(); // Limpiar features existentes
                newFeatureDataFromGeoJSON.forEach(item => {
                    vectorSource.current.addFeature(item.feature); // Añadir nuevas features a la fuente
                });
                vectorSource.current.changed(); // Forzar actualización de la fuente para features cargadas
            };
            reader.readAsText(file);
        }
    };


    const handleExportGeoJSON = () => {
        const features = vectorSource.current.getFeatures(); // Obtener features de la fuente
        const geojson = new GeoJSON().writeFeatures(features, {
            featureProjection: 'EPSG:4326', // Proyección de las features en el mapa
            dataProjection: 'EPSG:4326'    // Proyección deseada para la salida
        });
        console.log("GeoJSON Exportado:", geojson);
        return geojson;
    };


    const handleCloseModal = () => {
        const geojsonData = handleExportGeoJSON();
        onClose(geojsonData);
    };


    const handleDrawTypeChange = (event) => {
        addDrawInteraction(event.target.value);
    };

    const handleWKTListItemClick = (index) => {
        setSelectedWKTIndex(index);
        const selectedItem = featureData[index];
        if (selectedItem && selectedItem.feature) {
            // Resaltar la geometría usando la feature directamente
            highlightSource.clear();
            highlightSource.addFeature(selectedItem.feature);
            
            // Seleccionar la feature en el mapa a través de la interacción de selección
            if (selectInteractionRef.current) {
                selectInteractionRef.current.getFeatures().clear();
                selectInteractionRef.current.getFeatures().push(selectedItem.feature);
            }
        }
    };

    const highlightGeometry = (feature) => {
        highlightSource.clear();
        if (!feature) return;
        highlightSource.addFeature(feature);
    };

    const handleFeatureSelectOnMap = (selectedFeatures) => {
        console.log('handleFeatureSelectOnMap llamado con:', selectedFeatures);
        console.log('featureData actual:', featureData);
        
        if (selectedFeatures && selectedFeatures.length > 0) {
            const selectedFeature = selectedFeatures[0];
            
            // Obtener el ID de la feature seleccionada
            const selectedFeatureId = selectedFeature.getId();
            console.log('Feature seleccionada en el mapa:', selectedFeature);
            console.log('ID de la feature seleccionada:', selectedFeatureId);
            
            if (!selectedFeatureId) {
                console.warn('La feature seleccionada no tiene un ID.');
                return;
            }
            
            // Encontrar el índice en featureData comparando IDs
            const selectedIndex = featureData.findIndex(item => {           
                if (!item || !item.feature) return false;  // Add null check
                const itemFeatureId = item.feature.getId();
                const match = itemFeatureId && itemFeatureId.toString() === selectedFeatureId.toString();
                return match;
            });
            
            console.log('Índice encontrado:', selectedIndex, 'de', featureData.length, 'features');
            
            if (selectedIndex !== -1) {
                setSelectedWKTIndex(selectedIndex);
                highlightGeometry(selectedFeature);
                
                // Asegurarse de que el elemento de la lista sea visible
                const listItem = document.querySelector(`li[data-feature-id="${selectedFeatureId}"]`);
                if (listItem) {
                    listItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            } else {
                console.warn('No se encontró la feature en featureData con el ID seleccionado. Todas las features:', 
                    featureData.map(f => {
                        // Verificar si f o f.feature son null/undefined
                        if (!f || !f.feature) {
                            return {
                                id: f?.id || 'sin id',
                                featureId: 'feature no disponible',
                                type: 'tipo no disponible'
                            };
                        }
                        return {
                            id: f.id, 
                            featureId: f.feature.getId(),
                            type: f.feature.getGeometry()?.getType() || 'tipo desconocido'
                        };
                    })
                );
                setSelectedWKTIndex(null);
                highlightSource.clear();
            }
        } else {
            console.log('Ninguna feature seleccionada');
            setSelectedWKTIndex(null);
            highlightSource.clear();
            if (selectInteractionRef.current) {
                selectInteractionRef.current.getFeatures().clear();
            }
        }
    };

    return (
        <div className="h-full flex flex-col">            
            <div ref={mapRef} className="w-full flex-grow border border-gray-300 relative">
                <div className="absolute top-2 left-2 bg-white p-2 rounded shadow-md z-10">
                    <input type="file" accept=".geojson" onChange={handleFileUpload} className="border border-gray-300 rounded px-2 py-1 block mb-2" />
                    <select
                        onChange={handleDrawTypeChange}
                        value={selectedDrawType} // Controlar el select con el estado
                        className="border border-gray-300 rounded px-2 py-1 block mb-2"
                    >
                        <option value="none">Seleccionar Dibujo</option>
                        <option value="Point">Punto</option>
                        <option value="LineString">Línea</option>
                        <option value="Polygon">Polígono</option>
                    </select>
                </div>

                {drawnFeatures.length > 0 && (
                    <div className="absolute top-2 right-2 w-64 bg-white bg-opacity-90 rounded-lg shadow-lg z-10 overflow-hidden">
                        <div className="bg-gray-100 p-2 border-b border-gray-200">
                            <h3 className="font-semibold text-sm">Geometrías Dibujadas</h3>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            <ul className="divide-y divide-gray-200">
                                {renderWKTList()}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

export default MapView;

//CAMBIOS

// Notificar al componente padre con el formato correcto
    /* NO BORRAR
    if (onFeatureDataChange) {
        const geoJSONFormat = new GeoJSON();
        const featuresGeoJSON = newData.map(item => {
            const feature = item.feature;
            return {
                type: 'Feature',
                geometry: JSON.parse(geoJSONFormat.writeGeometry(feature.getGeometry())),
                properties: {
                    id: item.id,
                    ol_uid: item.ol_uid
                }
            };
        });
        
        const geoJSONData = {
            type: 'FeatureCollection',
            features: featuresGeoJSON
        };
        
        console.log('Enviando GeoJSON al componente padre:', geoJSONData);
        onFeatureDataChange(geoJSONData);
    }
    
    return newData;*/

    // ¡Aquí es donde llamas al callback para enviar los datos al padre!
    //onFeatureDataChange(newData); //NO USAR
